<script setup>
import { useForm, usePage, router, Head, Link } from '@inertiajs/vue3';
import { ref, watch, onMounted, onBeforeUnmount, computed, nextTick } from 'vue';
import { route } from 'ziggy-js';
import axios from 'axios';

const props = defineProps({
  jenisMobils: Array,
  errors: Object,
  flash: Object
});

const page = usePage();
const isEditing = ref(false);
const showForm = ref(false); // Toggle between list and form on mobile/if desired, or keep side-by-side like blade? Blade was side-by-side (col-5 form, col-7 table).

// Form Data
const form = useForm({
  id: '',
  jenis_nama: '',
  jenis_slug: '',
  jenis_deskripsi: '', // CKEditor
  jenis_artikel: '',   // CKEditor
  listing_meta_description: '',
  artikel_meta_description: '',
});

// CKEditor Instances
let deskripsiEditor = null;
let artikelEditor = null;

// Counters
const deskripsiWordCount = ref(0);
const listingCharCount = ref(0);
const artikelMetaCharCount = ref(0);

// Initialize CKEditors
const initEditors = () => {
    if (typeof CKEDITOR === 'undefined') {
        console.error('CKEditor not loaded');
        return;
    }

    // Destroy existing instances if any
    if (CKEDITOR.instances['jenis_deskripsi']) {
        try {
            CKEDITOR.instances['jenis_deskripsi'].destroy();
        } catch (e) { console.warn('CKEditor destroy error', e); }
    }
    if (CKEDITOR.instances['jenis_artikel']) {
        try {
            CKEDITOR.instances['jenis_artikel'].destroy();
        } catch (e) { console.warn('CKEditor destroy error', e); }
    }

    // Deskripsi Editor
    deskripsiEditor = CKEDITOR.replace('jenis_deskripsi', {
        height: 100,
        toolbar: [
            { name: 'basicstyles', items: ['Bold', 'Italic'] },
            { name: 'paragraph', items: ['NumberedList', 'BulletedList'] },
            { name: 'styles', items: ['Format'] },
            { name: 'links', items: ['Link', 'Unlink'] }
        ],
        format_tags: 'p;h3;h4;h5;h6'
    });

    deskripsiEditor.on('change', () => {
        form.jenis_deskripsi = deskripsiEditor.getData();
        updateDeskripsiCounter();
    });

    // Artikel Editor
    artikelEditor = CKEDITOR.replace('jenis_artikel', {
        height: 300,
        toolbar: [
            { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline', 'Strike'] },
            { name: 'paragraph', items: ['NumberedList', 'BulletedList', 'Blockquote'] },
            { name: 'styles', items: ['Format'] },
            { name: 'links', items: ['Link', 'Unlink'] },
            { name: 'insert', items: ['HorizontalRule'] },
            { name: 'undo', items: ['Undo', 'Redo'] }
        ]
    });

    artikelEditor.on('change', () => {
         form.jenis_artikel = artikelEditor.getData();
    });
};

const updateDeskripsiCounter = () => {
    if (!deskripsiEditor) return;
    const content = deskripsiEditor.getData();
    const plainText = content.replace(/<[^>]*>/g, '');
    deskripsiWordCount.value = plainText.trim() === '' ? 0 : plainText.trim().split(/\s+/).length;
};

onMounted(() => {
    // Wait for DOM to be ready for CKEditor replacement
    nextTick(() => {
        initEditors();
    });
});

onBeforeUnmount(() => {
    if (typeof CKEDITOR !== 'undefined') {
        if (CKEDITOR.instances['jenis_deskripsi']) {
            try {
                CKEDITOR.instances['jenis_deskripsi'].destroy();
            } catch (e) {
                console.warn('CKEditor destroy error', e);
            }
        }
        if (CKEDITOR.instances['jenis_artikel']) {
            try {
                CKEDITOR.instances['jenis_artikel'].destroy();
            } catch (e) {
                console.warn('CKEditor destroy error', e);
            }
        }
    }
    deskripsiEditor = null;
    artikelEditor = null;
});

// Actions
const errorModalContent = ref([]);
const originalData = ref(null);
const modalType = ref('error'); // 'error' | 'warning'
const saving = ref(false);

// Show modal helper
const showModal = (type, messages) => {
    modalType.value = type;
    errorModalContent.value = Array.isArray(messages) ? messages : [messages];
    try {
        const modal = new bootstrap.Modal(document.getElementById('errorModal'));
        modal.show();
    } catch (e) {
        console.error('Modal show error:', e);
    }
};

const submit = async () => {
    saving.value = true;
    
    // Short delay for spinner visibility
    await new Promise(resolve => setTimeout(resolve, 300));

    // Update form data from editors before submit (just in case)
    if (deskripsiEditor) form.jenis_deskripsi = deskripsiEditor.getData();
    if (artikelEditor) form.jenis_artikel = artikelEditor.getData();

    // Frontend validation
    const errors = [];
    if (!form.jenis_nama || form.jenis_nama.trim() === '') {
        errors.push('Nama Tipe Mobil wajib diisi.');
    }
    if (!form.jenis_deskripsi || form.jenis_deskripsi.trim() === '') {
        errors.push('Deskripsi wajib diisi.');
    }
    if (!form.jenis_artikel || form.jenis_artikel.trim() === '') {
        errors.push('Artikel wajib diisi.');
    }

    if (errors.length > 0) {
        saving.value = false;
        showModal('error', errors);
        return;
    }

    // Check for changes in edit mode
    if (isEditing.value) {
        if (originalData.value) {
            const currentData = {
                jenis_nama: form.jenis_nama,
                jenis_slug: form.jenis_slug,
                listing_meta_description: form.listing_meta_description,
                artikel_meta_description: form.artikel_meta_description
            };

            const simpleFieldsMatch = JSON.stringify(originalData.value) === JSON.stringify(currentData);
            
            // Check editors dirty state (CKEditor 4)
            const deskripsiDirty = deskripsiEditor ? deskripsiEditor.checkDirty() : false;
            const artikelDirty = artikelEditor ? artikelEditor.checkDirty() : false;

            if (simpleFieldsMatch && !deskripsiDirty && !artikelDirty) {
                saving.value = false;
                showModal('warning', 'Tidak ada perubahan data yang disimpan.');
                return;
            }
        }
    }

    saving.value = true;

    const options = {
        preserveScroll: true,
        onSuccess: () => {
            saving.value = false;
            resetForm();
        },
        onError: (errors) => {
            saving.value = false;
            // Collect all error messages into a flat array
            showModal('error', Object.values(errors).flat());
        },
        onFinish: () => {
            saving.value = false;
        }
    };

    if (isEditing.value) {
        form.put(route('admin.jenis-mobil.update', form.id), options);
    } else {
        form.post(route('admin.jenis-mobil.store'), options);
    }
};

const edit = (item) => {
    isEditing.value = true;
    form.id = item.id;
    form.jenis_nama = item.jenis_nama;
    form.jenis_slug = item.jenis_slug || '';
    form.jenis_deskripsi = item.jenis_deskripsi || '';
    form.jenis_artikel = item.jenis_artikel || '';
    form.listing_meta_description = item.listing_meta_description || '';
    form.artikel_meta_description = item.artikel_meta_description || '';

    // Store original data for comparison (exclude editors, checks via checkDirty)
    originalData.value = JSON.parse(JSON.stringify({
        jenis_nama: form.jenis_nama,
        jenis_slug: form.jenis_slug,
        listing_meta_description: form.listing_meta_description,
        artikel_meta_description: form.artikel_meta_description
    }));

    // Set editor data
    if (deskripsiEditor) {
        deskripsiEditor.setData(item.jenis_deskripsi || '', function() {
            this.resetDirty();
            setTimeout(updateDeskripsiCounter, 100);
        });
    }
    
    if (artikelEditor) {
        artikelEditor.setData(item.jenis_artikel || '', function() {
            this.resetDirty();
        });
    }
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

const deleteItem = (id) => {
    if (confirm('Apakah Anda yakin ingin menghapus data ini?')) {
        router.delete(route('admin.jenis-mobil.destroy', id));
    }
};

// Delete Logic
const deleteForm = useForm({});
const deleteId = ref(null);
const deleteName = ref('');
const deleteConfirmedCheck = ref(false);

const confirmDelete = (item) => {
    deleteId.value = item.id;
    deleteName.value = item.jenis_nama;
    deleteConfirmedCheck.value = false;
    const modal = new bootstrap.Modal(document.getElementById('deleteModal'));
    modal.show();
};

const submitDelete = () => {
    if (!deleteId.value) return;
    deleteForm.delete(route('admin.jenis-mobil.destroy', deleteId.value), {
        preserveScroll: true,
        onSuccess: () => {
            const modalEl = document.getElementById('deleteModal');
            const modal = bootstrap.Modal.getInstance(modalEl);
            if (modal) modal.hide();
        }
    });
};

const processingStatusId = ref(null);
const togglingId = ref(null);

const toggleStatus = async (item) => {
    togglingId.value = item.hash_id;
    try {
        const response = await axios.patch(`/admin/jenis-mobil/${item.hash_id}/toggle-status`);
        if (response.data.status === 'success' || response.data.jenis_status !== undefined) {
            item.jenis_status = response.data.jenis_status ?? !item.jenis_status;
        }
    } catch (error) {
        console.error('Toggle status error:', error);
    } finally {
        togglingId.value = null;
    }
};

// Slugify Helper
const slugify = (text) => {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')        // Replace spaces with -
    .replace(/[^\w\-]+/g, '')    // Remove all non-word chars
    .replace(/\-\-+/g, '-')      // Replace multiple - with single -
    .replace(/^-+/, '')          // Trim - from start of text
    .replace(/-+$/, '');         // Trim - from end of text
};

// Watcher to auto-generate slug and meta descriptions
watch(() => form.jenis_nama, (newValue) => {
    if (!isEditing.value) {
         // Auto-generate Slug
         form.jenis_slug = slugify(newValue);

         // Auto-generate Meta Description Listing (Target: < 150 chars)
         // Keyword: "sewa mobil banyuwangi"
         if (newValue) {
            // Shortened to ensure < 150 chars even with long names
            form.listing_meta_description = `Sewa Mobil Banyuwangi ${newValue} Murah. Unit bersih dan terawat, bisa lepas kunci atau dengan sopir untuk perjalanan Anda.`;
            
            // Auto-generate Meta Description Artikel
            form.artikel_meta_description = `Ulasan ${newValue} - Sewa Mobil Banyuwangi. Info spesifikasi, fitur, dan harga sewa ${newValue} terbaik untuk liburan di Banyuwangi.`;
         } else {
            form.listing_meta_description = '';
            form.artikel_meta_description = '';
         }
    }
});

const resetForm = () => {
    isEditing.value = false;
    originalData.value = null;
    form.reset();
    form.clearErrors();
    if (deskripsiEditor) deskripsiEditor.setData('');
    if (artikelEditor) artikelEditor.setData('');
    deskripsiWordCount.value = 0;
};


// View Modal Logic
const showDescModal = ref(false);
const modalContent = ref('');
const modalTitle = ref('');




const modalItem = ref({});

const viewArtikel = (item) => {
    modalTitle.value = 'Detail Artikel & SEO';
    modalItem.value = item;
    modalContent.value = item.jenis_artikel || '<em>Tidak ada data artikel.</em>';
    const modal = new bootstrap.Modal(document.getElementById('viewModal'));
    modal.show();
};

</script>

<template>
    <Head title="Kelola Jenis Mobil" />

    <div class="container-full">
        <!-- Breadcrumb Section -->
        <div class="content-header">
            <div class="d-flex align-items-center justify-content-between">
                <div class="d-block">
                    <h4 class="page-title">Kelola Jenis Mobil</h4>
                    <div class="d-inline-block align-items-center">
                        <nav>
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item"><Link href="/admin/dashboard"><i class="mdi mdi-home-outline"></i></Link></li>
                                <li class="breadcrumb-item active" aria-current="page">Jenis Mobil</li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </div>
        </div>

        <section class="content">
            <div class="row">
                <!-- Form Section (Top) -->
                <div class="col-12">
                    <div class="box">
                        <div class="box-header with-border">
                            <h4 class="box-title text-primary"><i class="ti-car me-2"></i>Form Jenis Mobil</h4>
                        </div>
                        <form @submit.prevent="submit">
                            <div class="box-body">
                                <div class="row">
                                    <!-- Left Column: Inputs & Meta -->
                                    <div class="col-xl-4 col-lg-5 col-12">
                                        <div class="form-group mb-3">
                                            <label for="jenis_nama" class="form-label">Tipe Mobil <span class="text-danger">*</span></label>
                                            <input type="text" id="jenis_nama" v-model="form.jenis_nama" class="form-control" :class="{ 'is-invalid': form.errors.jenis_nama }" placeholder="Contoh: MPV Family" autocomplete="off">
                                            <div class="invalid-feedback">{{ form.errors.jenis_nama }}</div>
                                        </div>
                                        
                                        <div class="form-group mb-3">
                                            <label for="jenis_slug" class="form-label">Slug</label>
                                            <input type="text" id="jenis_slug" v-model="form.jenis_slug" class="form-control" :class="{ 'is-invalid': form.errors.jenis_slug }" placeholder="Contoh: mpv-family" autocomplete="off">
                                            <div class="invalid-feedback">{{ form.errors.jenis_slug }}</div>
                                        </div>

                                        <!-- SEO Meta Data -->
                                        <div class="card bg-light-light mb-0 mt-3">
                                            <div class="card-body py-3">
                                                <h5 class="text-primary mb-3"><i class="ti-world me-2"></i>SEO Meta Data</h5>
                                                
                                                <div class="form-group mb-3">
                                                    <div class="d-flex justify-content-between">
                                                        <label for="listing_meta_description" class="form-label">Meta Description - Listing</label>
                                                        <small :class="form.listing_meta_description?.length > 155 ? 'text-danger' : 'text-muted'">{{ form.listing_meta_description?.length || 0 }}/155</small>
                                                    </div>
                                                    <textarea id="listing_meta_description" v-model="form.listing_meta_description" class="form-control" rows="4" maxlength="155" :class="{ 'is-invalid': form.errors.listing_meta_description }" autocomplete="off"></textarea>
                                                    <div class="invalid-feedback">{{ form.errors.listing_meta_description }}</div>
                                                    <small class="text-muted d-block mt-1">Deskripsi yang muncul di halaman list mobil.</small>
                                                </div>

                                                <div class="form-group mb-3">
                                                    <div class="d-flex justify-content-between">
                                                        <label for="artikel_meta_description" class="form-label">Meta Description - Artikel</label>
                                                        <small :class="form.artikel_meta_description?.length > 155 ? 'text-danger' : 'text-muted'">{{ form.artikel_meta_description?.length || 0 }}/155</small>
                                                    </div>
                                                    <textarea id="artikel_meta_description" v-model="form.artikel_meta_description" class="form-control" rows="4" maxlength="155" :class="{ 'is-invalid': form.errors.artikel_meta_description }" autocomplete="off"></textarea>
                                                    <div class="invalid-feedback">{{ form.errors.artikel_meta_description }}</div>
                                                    <small class="text-muted d-block mt-1">Deskripsi yang muncul di halaman detail artikel.</small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Right Column: Editors -->
                                    <div class="col-xl-8 col-lg-7 col-12">
                                        <div class="form-group mb-4">
                                            <label for="jenis_deskripsi" class="form-label">Deskripsi <span class="text-danger">*</span></label>
                                            <textarea id="jenis_deskripsi" name="jenis_deskripsi" class="form-control" :class="{ 'is-invalid': form.errors.jenis_deskripsi }"></textarea>
                                            <div class="d-flex justify-content-between align-items-center mt-1">
                                                <div class="invalid-feedback d-block" v-if="form.errors.jenis_deskripsi">{{ form.errors.jenis_deskripsi }}</div>
                                                <small class="text-muted ms-auto">
                                                    <span :class="{ 'text-danger': deskripsiWordCount > 500, 'text-warning': deskripsiWordCount > 450 }">{{ deskripsiWordCount }}</span>/500 kata
                                                </small>
                                            </div>
                                        </div>

                                        <div class="form-group">
                                            <label for="jenis_artikel" class="form-label">Artikel <span class="text-danger">*</span></label>
                                            <textarea id="jenis_artikel" name="jenis_artikel" class="form-control" :class="{ 'is-invalid': form.errors.jenis_artikel }"></textarea>
                                            <div class="invalid-feedback d-block" v-if="form.errors.jenis_artikel">{{ form.errors.jenis_artikel }}</div>
                                            <small class="text-muted">Gunakan editor ini untuk membuat konten artikel yang lengkap.</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="box-footer text-end">
                                <button type="button" class="btn btn-warning me-2" v-if="isEditing" @click="resetForm"><i class="ti-trash me-2"></i> Batal</button>
                                <button type="submit" class="btn btn-primary" :disabled="saving">
                                    <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
                                    <i v-else class="ti-save-alt me-2"></i>
                                    {{ saving ? 'Menyimpan...' : (isEditing ? 'Simpan Perubahan' : 'Simpan') }}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <!-- List Section (Bottom) -->
            <div class="row mt-4">
                <div class="col-12">
                     <div class="box">
                        <div class="box-header with-border">
                            <h4 class="box-title text-primary"><i class="ti-view-list me-2"></i>Daftar Jenis Mobil</h4>
                        </div>
                        <div class="box-body">
                            <div class="table-responsive">
                                <table class="table table-hover mb-0">
                                    <thead>
                                        <tr>
                                            <th>Tipe</th>
                                            <th>Deskripsi</th>
                                            <th>Artikel</th>
                                            <th>Status</th>
                                            <th class="text-center">Aksi</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-if="jenisMobils.length === 0">
                                            <td colspan="5" class="text-center">Belum ada data jenis mobil.</td>
                                        </tr>
                                        <tr v-for="item in jenisMobils" :key="item.hash_id" class="align-middle">
                                            <td class="fw-bold">{{ item.jenis_nama }}</td>
                                            <td v-html="item.jenis_deskripsi"></td>
                                            <td>
                                                <button type="button" class="btn btn-success btn-sm" @click="viewArtikel(item)" title="Lihat Artikel"><i class="fa fa-file-text"></i> Lihat</button>
                                            </td>
                                            <td>
                                                <button type="button" class="btn btn-sm rounded-1 px-3" :class="item.jenis_status ? 'btn-success' : 'btn-danger'" @click="toggleStatus(item)" :title="item.jenis_status ? 'Nonaktifkan' : 'Aktifkan'" :disabled="togglingId === item.hash_id">
                                                    <span v-if="togglingId === item.hash_id" class="spinner-border spinner-border-sm me-1"></span>
                                                    <i v-else class="fa me-1" :class="item.jenis_status ? 'fa-check-circle' : 'fa-times-circle'"></i>
                                                    {{ item.jenis_status ? 'Aktif' : 'Nonaktif' }}
                                                </button>
                                            </td>
                                            <td class="text-center">
                                                <div class="btn-group">
                                                    <button type="button" class="btn btn-primary btn-sm" @click="edit(item)" title="Ubah"><i class="fa fa-edit"></i></button>
                                                    <button type="button" class="btn btn-danger btn-sm" @click="confirmDelete(item)" title="Hapus"><i class="fa fa-trash"></i></button>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- View Modal -->
        <div class="modal fade" id="viewModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header bg-primary text-white">
                        <h5 class="modal-title"><i class="fa fa-file-text me-2"></i>{{ modalTitle }}</h5>
                        <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <!-- Metadata Info -->
                        <div class="list-group-item list-group-item-info p-3 rounded mb-3">
                            <table class="table table-sm table-borderless mb-0">
                                <tr class="align-top">
                                    <td class="fw-bold text-dark pe-3" style="width: 1%; white-space: nowrap;">Tipe Mobil</td>
                                    <td class="text-center" style="width: 1%; white-space: nowrap;">:</td>
                                    <td class="fw-bold text-primary">{{ modalItem.jenis_nama }}</td>
                                </tr>
                                <tr class="align-top">
                                    <td class="fw-bold text-dark pe-3" style="white-space: nowrap;">Slug</td>
                                    <td class="text-center">:</td>
                                    <td class="text-danger">/{{ modalItem.jenis_slug }}</td>
                                </tr>
                                <tr class="align-top">
                                    <td class="fw-bold text-dark pe-3" style="white-space: nowrap;">Meta Listing</td>
                                    <td class="text-center">:</td>
                                    <td>{{ modalItem.listing_meta_description || '-' }}</td>
                                </tr>
                                <tr class="align-top">
                                    <td class="fw-bold text-dark pe-3" style="white-space: nowrap;">Meta Artikel</td>
                                    <td class="text-center">:</td>
                                    <td>{{ modalItem.artikel_meta_description || '-' }}</td>
                                </tr>
                            </table>
                        </div>
                        
                        <h5 class="text-primary border-bottom pb-2 mb-3"><i class="ti-file me-2"></i>Konten Artikel</h5>
                        <div v-html="modalContent"></div>
                    </div>
                    <div class="modal-footer text-end">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal"><i class="fa fa-times me-2"></i>Tutup</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Delete Confirmation Modal -->
        <div class="modal fade" id="deleteModal" tabindex="-1" aria-labelledby="deleteModalLabel" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header bg-danger text-white">
                        <h5 class="modal-title" id="deleteModalLabel"><i class="fa fa-trash me-2"></i>Konfirmasi Hapus</h5>
                        <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <p>Apakah Anda yakin ingin menghapus Jenis Mobil <strong>{{ deleteName }}</strong>?</p>
                        <p class="text-danger small mb-3">Tindakan ini tidak dapat dibatalkan.</p>
                        
                        <div class="form-check list-group-item list-group-item-danger p-3 rounded mb-0">
                            <input class="form-check-input" type="checkbox" id="confirmCheck" v-model="deleteConfirmedCheck">
                            <label class="form-check-label text-dark" for="confirmCheck">
                                Saya mengerti dan yakin ingin menghapus data ini.
                            </label>
                        </div>
                    </div>
                    <div class="modal-footer text-end">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal"><i class="fa fa-times me-2"></i>Batal</button>
                        <button type="button" class="btn btn-danger" :disabled="!deleteConfirmedCheck" @click="submitDelete">
                            <i class="fa fa-trash me-2"></i>Ya, Hapus
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Error/Warning Modal -->
        <div class="modal fade" id="errorModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header text-white" :class="modalType === 'warning' ? 'bg-warning text-dark' : 'bg-danger'">
                        <h5 class="modal-title">
                            <i class="me-2" :class="modalType === 'warning' ? 'fa fa-exclamation-triangle' : 'ti-alert'"></i>
                            {{ modalType === 'warning' ? 'Peringatan' : 'Peringatan Validasi' }}
                        </h5>
                        <button type="button" class="btn-close" :class="modalType === 'warning' ? '' : 'btn-close-white'" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <p class="mb-3" v-if="modalType === 'error'">Mohon perbaiki kesalahan berikut sebelum menyimpan:</p>
                        <p class="mb-3" v-else>Informasi sistem:</p>
                        <ul class="list-group list-group-flush">
                            <li class="list-group-item" :class="modalType === 'warning' ? 'list-group-item-warning' : 'list-group-item-danger'" v-for="(error, index) in errorModalContent" :key="index">
                                <i class="fa me-2" :class="modalType === 'warning' ? 'fa-info-circle' : 'fa-exclamation-circle'"></i>{{ error }}
                            </li>
                        </ul>
                    </div>
                    <div class="modal-footer text-end">
                        <button type="button" class="btn" :class="modalType === 'warning' ? 'btn-warning' : 'btn-danger'" data-bs-dismiss="modal"><i class="fa fa-check-square-o me-2"></i>Mengerti</button>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<style scoped>
.table td :deep(p) {
    margin-bottom: 0;
}

/* Limit modal height so it is not too tall, but still scrollable */


</style>
